/**
 * Pending Withdrawals Modal
 * 
 * Displays warning modal when user has pending withdrawals before deposit.
 * Allows user to cancel withdrawals before proceeding with deposit.
 * 
 * @package betbeast
 */

export class PendingWithdrawalsModal {
    constructor() {
        this.modal = null;
        this.proceedCallback = null;
        this.cancelCallback = null;
        this.initialized = false;
    }
    
    /**
     * Initialize the pending withdrawals modal
     */
    init() {
        this.modal = document.getElementById('deposit-modal-pending-withdrawal');
        
        if (!this.modal) {
            return false;
        }
        
        this.setupEventListeners();
        this.initialized = true;
        return true;
    }
    
    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Overlay click (close modal and proceed to deposit)
        const overlays = this.modal.querySelectorAll('[data-exit-intent-close]');
        overlays.forEach(overlay => {
            overlay.addEventListener('click', () => this.handleProceed());
        });
        
        // Individual cancel withdrawal buttons
        const cancelButtons = this.modal.querySelectorAll('[data-cancel-withdrawal]');
        cancelButtons.forEach(button => {
            button.addEventListener('click', (e) => this.handleCancelWithdrawal(e));
        });
        
        // Cancel all withdrawals button
        const cancelAllBtn = this.modal.querySelector('[data-cancel-all-withdrawals]');
        if (cancelAllBtn) {
            cancelAllBtn.addEventListener('click', () => this.handleCancelAllWithdrawals());
        }
    }
    
    /**
     * Open the pending withdrawals modal
     * @param {Function} onProceed - Callback when user proceeds to deposit
     * @param {Function} onCancel - Callback when user cancels (optional)
     */
    open(onProceed, onCancel = null) {
        if (!this.modal) {
            console.warn('Cannot open pending withdrawals modal - not initialized');
            return;
        }
        
        this.proceedCallback = onProceed;
        this.cancelCallback = onCancel;
        
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
    }
    
    /**
     * Close the pending withdrawals modal
     */
    close() {
        if (this.modal) {
            this.modal.classList.remove('is-open');
            this.modal.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('modal-open');
        }
        
        this.proceedCallback = null;
        this.cancelCallback = null;
    }
    
    /**
     * Handle proceed to deposit action
     */
    handleProceed() {
        const callback = this.proceedCallback;
        this.close();
        
        if (callback) {
            callback();
        }
    }
    
    /**
     * Handle cancel action
     */
    handleCancel() {
        const callback = this.cancelCallback;
        this.close();
        
        if (callback) {
            callback();
        }
    }
    
    /**
     * Handle canceling a single withdrawal
     * @param {Event} event - Click event
     */
    async handleCancelWithdrawal(event) {
        const button = event.currentTarget;
        const paymentId = button.dataset.paymentId;
        
        if (!paymentId) {
            console.error('No payment ID found for withdrawal cancellation');
            return;
        }
        
        // Disable button to prevent double clicks
        button.disabled = true;
        button.textContent = 'Canceling...';
        
        try {
            const response = await this.cancelWithdrawal(paymentId);
            
            if (response.success) {
                // Remove the withdrawal item from DOM
                const withdrawalItem = button.closest('.deposit-modal-pending-withdrawal__withdrawal-item');
                if (withdrawalItem) {
                    withdrawalItem.remove();
                }
                
                // Check if there are any remaining withdrawals
                const remainingWithdrawals = this.modal.querySelectorAll('.deposit-modal-pending-withdrawal__withdrawal-item');
                if (remainingWithdrawals.length === 0) {
                    // No more pending withdrawals - proceed to deposit
                    this.handleProceed();
                }
            } else {
                // Re-enable button on error
                button.disabled = false;
                button.textContent = 'Cancel';
                alert('Failed to cancel withdrawal. Please try again.');
            }
        } catch (error) {
            console.error('Error canceling withdrawal:', error);
            button.disabled = false;
            button.textContent = 'Cancel';
            alert('An error occurred. Please try again.');
        }
    }
    
    /**
     * Handle canceling all withdrawals
     */
    async handleCancelAllWithdrawals() {
        const withdrawalItems = this.modal.querySelectorAll('.deposit-modal-pending-withdrawal__withdrawal-item');
        const paymentIds = Array.from(withdrawalItems).map(item => item.dataset.paymentId).filter(id => id);
        
        if (paymentIds.length === 0) {
            console.warn('No withdrawals to cancel');
            return;
        }
        
        // Disable all cancel buttons
        const cancelButtons = this.modal.querySelectorAll('[data-cancel-withdrawal], [data-cancel-all-withdrawals]');
        cancelButtons.forEach(btn => {
            btn.disabled = true;
            if (btn.hasAttribute('data-cancel-all-withdrawals')) {
                btn.textContent = 'Canceling...';
            }
        });
        
        try {
            // Cancel all withdrawals in parallel
            const cancelPromises = paymentIds.map(paymentId => this.cancelWithdrawal(paymentId));
            const results = await Promise.all(cancelPromises);
            
            const allSuccessful = results.every(result => result.success);
            
            if (allSuccessful) {
                // Proceed to deposit
                this.handleProceed();
            } else {
                alert('Some withdrawals could not be canceled. Please try again.');
                // Re-enable buttons
                cancelButtons.forEach(btn => {
                    btn.disabled = false;
                    if (btn.hasAttribute('data-cancel-all-withdrawals')) {
                        btn.textContent = 'Cancel all';
                    }
                });
            }
        } catch (error) {
            console.error('Error canceling all withdrawals:', error);
            alert('An error occurred. Please try again.');
            // Re-enable buttons
            cancelButtons.forEach(btn => {
                btn.disabled = false;
                if (btn.hasAttribute('data-cancel-all-withdrawals')) {
                    btn.textContent = 'Cancel all';
                }
            });
        }
    }
    
    /**
     * Cancel a withdrawal via AJAX
     * @param {string} paymentId - Payment ID to cancel
     * @returns {Promise<Object>} Response object
     */
    async cancelWithdrawal(paymentId) {
        const formData = new FormData();
        formData.append('action', 'cancel_withdrawal');
        formData.append('payment_id', paymentId);
        formData.append('nonce', window.accountData?.nonce || '');
        
        const response = await fetch(window.accountData?.ajaxUrl || '/wp-admin/admin-ajax.php', {
            method: 'POST',
            body: formData
        });
        
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        
        return await response.json();
    }
    
    /**
     * Check if modal exists
     * @returns {boolean}
     */
    exists() {
        return !!this.modal;
    }
    
    /**
     * Check if initialized
     * @returns {boolean}
     */
    isInitialized() {
        return this.initialized;
    }
}
